import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, categories } from '@/mocks/articles';
import { formatViews, formatDate } from '@/utils/format';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug) || categories[0];
  const currentSlug = category.slug;

  const filteredArticles = articles
    .filter((a) => a.category === currentSlug)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 蜜桃传媒`,
    description: category.description,
    url: `https://mitaomedia.com/category/${currentSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          name: article.title,
          url: `https://mitaomedia.com/article/${article.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 分类浏览`}
        description={category.description}
        canonicalUrl={`https://mitaomedia.com/category/${currentSlug}`}
        schemaJson={schema}
      />

      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: '分类浏览', href: '/category/latest' },
          { label: category.name },
        ]} />

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-500">{category.description}</p>
          </div>

          <div className="bg-background-100 rounded-lg px-1 py-1 mb-8 inline-flex flex-wrap gap-1" role="tablist" aria-label="内容分类">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                role="tab"
                aria-selected={cat.slug === currentSlug}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                  cat.slug === currentSlug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <article key={article.id} className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer" data-product-shop>
                  <Link to={`/article/${article.id}`} className="block">
                    <div className="relative w-full h-52 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={`${article.title} - 蜜桃传媒${category.name}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h2 className="font-heading text-base font-semibold text-foreground-950 mb-3 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-4">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{formatDate(article.date)}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {formatViews(article.views)}
                        </span>
                      </div>
                      {article.tags && article.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {article.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">暂无相关内容，请浏览其他分类</p>
            </div>
          )}
        </section>
      </main>
    </>
  );
}